import { WidgetProps } from '@rjsf/utils';
import { useMemo } from 'react';
import { AddCircle } from '@mui/icons-material';
import { PillIconButton } from 'components/button/PillIconButton';
import useDialog from '../../hooks/useDialog.tsx';
import { useParams } from 'react-router';
import ViewDialog from '../dialog/ViewDialog.tsx';
import Loader from '../Loader.tsx';
import ListSelectAutocomplete from './ListSelectAutocomplete.tsx';
import useSecrets from 'api/secrets/useSecrets.ts';
import SecretAdd from 'views/secrets/SecretAdd.tsx';
import SecretRenderer from './CustomRenderers/SecretRenderer.tsx';
import { FormBlockContext } from './FormBlock.tsx';
import { Secret, SecretType } from 'api/model.ts';

type SecretWidgetProps = {
  types?: SecretType[];
};

const SecretWidget = (props: WidgetProps<SecretWidgetProps>) => {
  const { projectId } = useParams();
  const options = props.options as SecretWidgetProps;
  const [showAddSecretDialog, AddSecretDialog] = useDialog(ViewDialog);

  const contextSecrets = (props.formContext as FormBlockContext | undefined)?.secrets;
  const { secrets: fetchedSecrets, isFetching } = useSecrets(projectId, true, !contextSecrets);
  const secrets = contextSecrets ?? fetchedSecrets;

  const secretsById = useMemo(() => new Map((secrets ?? []).map((s) => [s.id, s])), [secrets]);

  const secretsOptions = useMemo(
    () =>
      secrets
        ?.filter((s) => !options.types || options.types.includes(s.type))
        .map((s) => ({
          value: s.name,
          const: s.id,
        })) ?? [],
    [secrets, options.types],
  );

  const handleShowAddSecret = () => {
    showAddSecretDialog(true);
  };

  const handleCreateNewSecret = (secret: Secret) => {
    if (!options.types || options.types.includes(secret.type)) {
      props.onChange(secret.id);
    }
    showAddSecretDialog(false);
  };

  return (
    <>
      <div className="flex w-100 flex-row items-center gap-2">
        {!isFetching ? (
          <ListSelectAutocomplete
            options={secretsOptions}
            value={props.value}
            onChange={props.onChange}
            label={props.schema.title}
            required={props.required}
            disabled={props.disabled || props.readonly || isFetching}
            render={(secretId) => <SecretRenderer secret={secretsById.get(secretId)} fallback={secretId} />}
          />
        ) : (
          <Loader />
        )}
        <PillIconButton
          color="info"
          title="Add secret"
          disabled={isFetching || props.disabled}
          onClick={handleShowAddSecret}
        >
          <AddCircle />
        </PillIconButton>
      </div>
      <AddSecretDialog>
        <SecretAdd
          projectId={projectId ?? ''}
          onSubmit={handleCreateNewSecret}
          onCancel={() => showAddSecretDialog(false)}
        />
      </AddSecretDialog>
    </>
  );
};
export default SecretWidget;
